'use client'

import React from 'react'
import { motion } from 'framer-motion'
import { Box, useTheme, useMediaQuery } from '@mui/material'
import { styled } from '@mui/system'
import { cyan } from '@mui/material/colors'

const StyledBox = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start', 
  minHeight: '50vh',
  paddingTop: theme.spacing(30), 
  overflow: 'hidden',
  width: '100%',
  borderRadius: theme.shape.borderRadius,
  zIndex: 0,
}));

const LampEffect = styled(motion.div)<{ conicposition?: string; gradientstops?: string }>(({ theme, conicposition = 'from 70deg at center top', gradientstops = `${cyan[500]}, transparent, transparent` }) => ({
  position: 'absolute',
  insetInline: 'auto',
  overflow: 'visible',
  backgroundImage: `conic-gradient(${conicposition}, ${gradientstops})`, 
  color: theme.palette.common.white,
}));


const LampShade = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '100%',
  left: 0,
  backgroundColor: theme.palette.background.default,
  bottom: 0,
  maskImage: 'linear-gradient(to top, white, transparent)',
}))

interface LampContainerProps {
  children: React.ReactNode
}

const Lamp: React.FC<LampContainerProps> = ({ children }) => {
  const theme = useTheme()
  const isXs = useMediaQuery(theme.breakpoints.down('sm'))
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'))
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'))

  const getLampSize = () => {
    if (isXs) return { initial: '8rem', final: '12rem', height: '14rem' }
    if (isSm) return { initial: '10rem', final: '20rem', height: '14rem' }
    if (isMd) return { initial: '12rem', final: '25rem', height: '14rem' }
    return { initial: '15rem', final: '30rem', height: '14rem' }
  }

  const { initial, final, height } = getLampSize()

  const getResponsiveValue = (values: { xs: string; sm: string; md: string; lg: string }) => {
    if (isXs) return values.xs
    if (isSm) return values.sm
    if (isMd) return values.md
    return values.lg
  }

  return (
    <StyledBox id="contact">
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          width: '100%',
          flex: 1,
          transform: 'scaleY(1.25)',
          alignItems: 'center',
          justifyContent: 'center',
          isolation: 'isolate',
          zIndex: 0,
        }}
      >
        <LampEffect
          initial={{ opacity: 0.5, width: initial }}
          whileInView={{ opacity: 1, width: final }}
          transition={{
            delay: 0.3,
            duration: 0.8,
            ease: 'easeInOut',
          }}
          style={{ right: '50%', height }}
        >
          <LampShade sx={{ height: `calc(${height} - 4rem)` }} />
          <Box
            sx={{
              position: 'absolute',
              width: { xs: '6rem', sm: '7rem', md: '8rem', lg: '10rem' },
              height: '100%',
              left: 0,
              backgroundColor: theme.palette.background.default,
              bottom: 0,
              maskImage: 'linear-gradient(to right, white, transparent)',
            }}
          />
        </LampEffect>
        <LampEffect
          initial={{ opacity: 0.5, width: initial }}
          whileInView={{ opacity: 1, width: final }}
          transition={{
            delay: 0.3,
            duration: 0.8,
            ease: 'easeInOut',
          }}
          style={{
            left: '50%',
            height,
            maskImage: 'linear-gradient(to right, white, transparent 100%)',
          }}
          conicposition="from 290deg at center top"
          gradientstops={`transparent, transparent, ${cyan[500]}`}
        >
          <Box
            sx={{
              position: 'absolute',
              width: { xs: '5rem', sm: '7rem', md: '8rem', lg: '10rem' },
              height: '100%',
              right: 0,
              backgroundColor: theme.palette.background.default,
              bottom: 0,
              maskImage: 'linear-gradient(to left, white, transparent)',
            }}
          />
          <LampShade sx={{ height: `calc(${height} - 4rem)` }} />
        </LampEffect>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            height: { xs: '8rem', sm: '10rem', md: '11rem', lg: '12rem' },
            width: '100%',
            transform: 'translateY(3rem) scaleX(1.5)',
            background: `linear-gradient(to bottom,
            transparent 0%,
            ${theme.palette.background.default} 40%)`,
            filter: 'blur(8px)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 'auto',
            zIndex: 50,
            height: '9rem',
            width: { xs: '12rem', sm: '20rem', md: '24rem', lg: '26rem' },
            transform: 'translateY(-50%)',
            borderRadius: '50%',
            background: `linear-gradient(to bottom,
            transparent 0%,
            ${cyan[500]} 50%)`,
            opacity: 0.5,
            filter: 'blur(12px)',
          }}
        />
        <motion.div
          initial={{ width: getResponsiveValue({ xs: '3rem', sm: '6rem', md: '7rem', lg: '8rem' }) }}
          whileInView={{ width: getResponsiveValue({ xs: '8rem', sm: '12rem', md: '14rem', lg: '16rem' }) }}
          transition={{
            delay: 0.3,
            duration: 0.8,
            ease: 'easeInOut',
          }}
          style={{
            position: 'absolute',
            inset: 'auto',
            height: '9rem',
            transform: 'translateY(-6rem)',
            borderRadius: '50%',
            background: `linear-gradient(to bottom,
            transparent 30%,
            ${cyan[300]} 100%)`,
            filter: 'blur(8px)',
          }}
        />
        <motion.div
          initial={{ width: getResponsiveValue({ xs: '5rem', sm: '10rem', md: '12rem', lg: '15rem' }) }}
          whileInView={{ width: getResponsiveValue({ xs: '12rem', sm: '20rem', md: '25rem', lg: '30rem' }) }}
          transition={{
            delay: 0.3,
            duration: 0.8,
            ease: 'easeInOut',
          }}
          style={{
            position: 'absolute',
            inset: 'auto',
            height: '0.125rem',
            transform: 'translateY(-7rem)',
            backgroundColor: cyan[300],
          }}
        />
      </Box>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {children}
      </Box>
    </StyledBox>
  )
}

export default Lamp;